import React, { useState, useEffect } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { Helmet } from 'react-helmet';
import { updateCredits } from '../slices/authSlice';
import { 
    ShareIcon,
    UserGroupIcon,
    CurrencyDollarIcon,
    ClipboardDocumentIcon,
    CheckCircleIcon,
    SparklesIcon,
    GiftIcon
} from '@heroicons/react/24/solid';
import { 
    StarIcon,
    ArrowRightIcon
} from '@heroicons/react/24/outline';
import useApiWithAuth from '../utils/useApiWithAuth';
import authFetch from '../utils/authFetch';

const Referral = () => {
    const token = useSelector((state) => state.auth.token);
    const user = useSelector((state) => state.auth.user);
    const userId = useSelector((state) => state.auth.user?.id);
    const { apiCall } = useApiWithAuth();
    const dispatch = useDispatch();
    
    const [referralCode, setReferralCode] = useState('');
    const [referralLink, setReferralLink] = useState('');
    const [stats, setStats] = useState({
        totalReferrals: 0,
        completedReferrals: 0,
        pendingReferrals: 0,
        totalCreditsEarned: 0,
        recentReferrals: []
    });
    const [isLoading, setIsLoading] = useState(true);
    const [copied, setCopied] = useState(false);

    useEffect(() => {
        fetchReferralData();
    }, []);

    const fetchReferralData = async () => {
        try {
            setIsLoading(true);
            
            // Récupérer le code de parrainage
            const codeResponse = await apiCall('https://cvanalyzerback.osc-fr1.scalingo.io/api/referrals/code');
            const codeData = await codeResponse.json();
            setReferralCode(codeData.referralCode);
            setReferralLink(codeData.referralLink);

            // Récupérer les statistiques
            const statsResponse = await apiCall('https://cvanalyzerback.osc-fr1.scalingo.io/api/referrals/stats');
            const statsData = await statsResponse.json();
            setStats(statsData);

            // Rafraîchir les crédits de l'utilisateur
            if (userId) {
                try {
                    const creditsResponse = await authFetch(`https://cvanalyzerback.osc-fr1.scalingo.io/api/users/${userId}/credits`);
                    if (creditsResponse.ok) {
                        const creditsData = await creditsResponse.json();
                        if (creditsData.credits !== undefined) {
                            dispatch(updateCredits(parseFloat(creditsData.credits)));
                            console.log('✅ Crédits mis à jour sur la page de parrainage:', creditsData.credits);
                        }
                    }
                } catch (error) {
                    console.error('Erreur lors du rafraîchissement des crédits:', error);
                }
            }
        } catch (error) {
            console.error('Erreur lors de la récupération des données:', error);
        } finally {
            setIsLoading(false);
        }
    };

    const copyToClipboard = async () => {
        try {
            await navigator.clipboard.writeText(referralLink);
            setCopied(true);
            setTimeout(() => setCopied(false), 2000);
        } catch (error) {
            console.error('Erreur lors de la copie:', error);
        }
    };

    const shareOnLinkedIn = () => {
        const text = encodeURIComponent(`J'utilise CV Analyse pour obtenir un score et des conseils concrets sur mon CV. Aperçu offert, rapport complet dès 5 € : ${referralLink}`);
        window.open(`https://www.linkedin.com/sharing/share-offsite/?url=${encodeURIComponent(referralLink)}`, '_blank');
    };

    const shareOnTwitter = () => {
        const text = encodeURIComponent(`J'utilise CV Analyse pour obtenir un score et des conseils concrets sur mon CV. Aperçu offert, rapport complet dès 5 € : ${referralLink}`);
        window.open(`https://twitter.com/intent/tweet?text=${text}`, '_blank');
    };

    const shareByEmail = () => {
        const subject = encodeURIComponent('Rejoins-moi sur CV Analyse');
        const body = encodeURIComponent(`Salut !\n\nJ'utilise CV Analyse pour scorer et améliorer mon CV.\n\nAvec mon lien : aperçu offert (score + 3 conseils) et 1 crédit bonus. Le rapport complet se débloque dès 5 €.\n\nLien : ${referralLink}\n\nÀ bientôt !`);
        window.location.href = `mailto:?subject=${subject}&body=${body}`;
    };

    if (isLoading) {
        return (
            <div className="page-shell flex items-center justify-center">
                <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
            </div>
        );
    }

    return (
        <div className="page-shell p-4">
            <Helmet>
                <title>Parrainage | CV Analyse</title>
                <meta name="robots" content="noindex, nofollow" />
            </Helmet>

            <div className="max-w-6xl mx-auto">
                {/* Header */}
                <div className="text-center mb-12">
                    <div className="flex items-center justify-center mb-4">
                        <div className="w-10 h-10 bg-blue-600 rounded-lg flex items-center justify-center mr-3">
                            <GiftIcon className="w-5 h-5 text-white" />
                        </div>
                        <h1 className="text-3xl font-semibold text-gray-900">
                            Parrainage
                        </h1>
                    </div>
                    <p className="text-xl text-gray-600 max-w-3xl mx-auto">
                        Parrainez vos amis et gagnez des crédits gratuits pour chacun d'eux !
                    </p>
                </div>

                {/* Comment ça marche */}
                <div className="bg-white rounded-3xl shadow-2xl p-8 md:p-12 mb-8">
                    <h2 className="text-2xl font-bold text-gray-800 mb-6 flex items-center">
                        <SparklesIcon className="w-6 h-6 mr-3 text-blue-600" />
                        Comment ça marche ?
                    </h2>
                    <div className="grid md:grid-cols-3 gap-6">
                        <div className="text-center">
                            <div className="w-16 h-16 bg-blue-100 rounded-full flex items-center justify-center mx-auto mb-4">
                                <span className="text-2xl font-bold text-blue-600">1</span>
                            </div>
                            <h3 className="font-semibold text-gray-800 mb-2">Partagez votre lien</h3>
                            <p className="text-gray-600 text-sm">
                                Partagez votre lien unique de parrainage avec vos amis
                            </p>
                        </div>
                        <div className="text-center">
                            <div className="w-16 h-16 bg-purple-100 rounded-full flex items-center justify-center mx-auto mb-4">
                                <span className="text-2xl font-bold text-purple-600">2</span>
                            </div>
                            <h3 className="font-semibold text-gray-800 mb-2">Ils s'inscrivent</h3>
                            <p className="text-gray-600 text-sm">
                                Vos amis s'inscrivent et reçoivent 1 analyse gratuite + 1 crédit bonus
                            </p>
                        </div>
                        <div className="text-center">
                            <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-4">
                                <span className="text-2xl font-bold text-green-600">3</span>
                            </div>
                            <h3 className="font-semibold text-gray-800 mb-2">Vous gagnez</h3>
                            <p className="text-gray-600 text-sm">
                                Quand ils font leur première analyse, vous recevez 1 crédit !
                            </p>
                        </div>
                    </div>
                </div>

                {/* Lien de parrainage */}
                <div className="bg-gradient-to-r from-blue-600 to-purple-600 rounded-3xl shadow-2xl p-8 md:p-12 mb-8 text-white">
                    <h2 className="text-2xl font-bold mb-6 flex items-center">
                        <ShareIcon className="w-6 h-6 mr-3" />
                        Votre lien de parrainage
                    </h2>
                    <div className="bg-white/10 backdrop-blur-sm rounded-2xl p-6 mb-6">
                        <div className="flex items-center gap-4 flex-wrap">
                            <div className="flex-1 min-w-[200px]">
                                <input
                                    type="text"
                                    value={referralLink}
                                    readOnly
                                    className="w-full px-4 py-3 bg-white/20 border border-white/30 rounded-xl text-white placeholder-white/70 focus:outline-none focus:ring-2 focus:ring-white/50"
                                />
                            </div>
                            <button
                                onClick={copyToClipboard}
                                className="px-6 py-3 bg-white text-blue-600 font-semibold rounded-xl hover:bg-blue-50 transition-all flex items-center gap-2"
                            >
                                {copied ? (
                                    <>
                                        <CheckCircleIcon className="w-5 h-5" />
                                        Copié !
                                    </>
                                ) : (
                                    <>
                                        <ClipboardDocumentIcon className="w-5 h-5" />
                                        Copier
                                    </>
                                )}
                            </button>
                        </div>
                    </div>

                    {/* Boutons de partage */}
                    <div className="flex flex-wrap gap-4 justify-center">
                        <button
                            onClick={shareOnLinkedIn}
                            className="px-6 py-3 bg-white/20 hover:bg-white/30 text-white font-semibold rounded-xl transition-all flex items-center gap-2"
                        >
                            <ShareIcon className="w-5 h-5" />
                            LinkedIn
                        </button>
                        <button
                            onClick={shareOnTwitter}
                            className="px-6 py-3 bg-white/20 hover:bg-white/30 text-white font-semibold rounded-xl transition-all flex items-center gap-2"
                        >
                            <ShareIcon className="w-5 h-5" />
                            Twitter
                        </button>
                        <button
                            onClick={shareByEmail}
                            className="px-6 py-3 bg-white/20 hover:bg-white/30 text-white font-semibold rounded-xl transition-all flex items-center gap-2"
                        >
                            <ShareIcon className="w-5 h-5" />
                            Email
                        </button>
                    </div>
                </div>

                {/* Statistiques */}
                <div className="grid md:grid-cols-2 gap-6 mb-8">
                    <div className="bg-white rounded-2xl shadow-xl p-6">
                        <div className="flex items-center justify-between mb-4">
                            <h3 className="text-xl font-bold text-gray-800 flex items-center">
                                <UserGroupIcon className="w-6 h-6 mr-2 text-blue-600" />
                                Vos parrainages
                            </h3>
                        </div>
                        <div className="space-y-4">
                            <div className="flex justify-between items-center">
                                <span className="text-gray-600">Total parrainés</span>
                                <span className="text-2xl font-bold text-blue-600">{stats.totalReferrals}</span>
                            </div>
                            <div className="flex justify-between items-center">
                                <span className="text-gray-600">Première analyse complétée</span>
                                <span className="text-xl font-semibold text-green-600">{stats.completedReferrals}</span>
                            </div>
                            <div className="flex justify-between items-center">
                                <span className="text-gray-600">En attente</span>
                                <span className="text-xl font-semibold text-orange-600">{stats.pendingReferrals}</span>
                            </div>
                        </div>
                    </div>

                    <div className="bg-white rounded-2xl shadow-xl p-6">
                        <div className="flex items-center justify-between mb-4">
                            <h3 className="text-xl font-bold text-gray-800 flex items-center">
                                <CurrencyDollarIcon className="w-6 h-6 mr-2 text-green-600" />
                                Crédits gagnés
                            </h3>
                        </div>
                        <div className="text-center">
                            <div className="text-4xl font-bold text-green-600 mb-2">
                                {stats.totalCreditsEarned.toFixed(0)}
                            </div>
                            <p className="text-gray-600">crédits gagnés au total</p>
                            <div className="mt-4 p-4 bg-green-50 rounded-xl">
                                <p className="text-sm text-gray-700">
                                    💡 Vous gagnez <strong>1 crédit</strong> pour chaque filleul qui complète sa première analyse
                                </p>
                            </div>
                        </div>
                    </div>
                </div>

                {/* Récompenses */}
                <div className="bg-gradient-to-r from-yellow-50 to-orange-50 rounded-3xl shadow-xl p-8 md:p-12 mb-8 border-2 border-yellow-200">
                    <h2 className="text-2xl font-bold text-gray-800 mb-6 flex items-center">
                        <StarIcon className="w-6 h-6 mr-3 text-yellow-500" />
                        Vos récompenses
                    </h2>
                    <div className="grid md:grid-cols-2 gap-6">
                        <div className="bg-white rounded-xl p-6">
                            <h3 className="font-semibold text-gray-800 mb-2">Pour votre filleul</h3>
                            <ul className="space-y-2 text-gray-600">
                                <li className="flex items-center gap-2">
                                    <CheckCircleIcon className="w-5 h-5 text-green-500" />
                                    <span>Aperçu offert (score + 3 conseils) + 1 crédit bonus</span>
                                </li>
                                <li className="flex items-center gap-2">
                                    <CheckCircleIcon className="w-5 h-5 text-green-500" />
                                    <span>Le rapport complet se débloque dès 5 €</span>
                                </li>
                            </ul>
                        </div>
                        <div className="bg-white rounded-xl p-6">
                            <h3 className="font-semibold text-gray-800 mb-2">Pour vous</h3>
                            <ul className="space-y-2 text-gray-600">
                                <li className="flex items-center gap-2">
                                    <CheckCircleIcon className="w-5 h-5 text-blue-500" />
                                    <span>+1 crédit quand votre filleul fait sa première analyse</span>
                                </li>
                                <li className="flex items-center gap-2">
                                    <CheckCircleIcon className="w-5 h-5 text-blue-500" />
                                    <span>Badge "Ambassadeur" après 5 parrainages</span>
                                </li>
                            </ul>
                        </div>
                    </div>
                </div>

                {/* Parrainages récents */}
                {stats.recentReferrals.length > 0 && (
                    <div className="bg-white rounded-2xl shadow-xl p-8">
                        <h2 className="text-2xl font-bold text-gray-800 mb-6">Parrainages récents</h2>
                        <div className="space-y-4">
                            {stats.recentReferrals.map((referral, index) => (
                                <div key={index} className="flex items-center justify-between p-4 bg-gray-50 rounded-xl">
                                    <div className="flex items-center gap-4">
                                        <div className="w-12 h-12 bg-gradient-to-r from-blue-500 to-purple-500 rounded-full flex items-center justify-center text-white font-bold">
                                            {referral.referred?.username?.charAt(0).toUpperCase() || 'U'}
                                        </div>
                                        <div>
                                            <p className="font-semibold text-gray-800">
                                                {referral.referred?.username || 'Utilisateur'}
                                            </p>
                                            <p className="text-sm text-gray-500">
                                                {new Date(referral.createdAt).toLocaleDateString('fr-FR')}
                                            </p>
                                        </div>
                                    </div>
                                    <div className="flex items-center gap-4">
                                        {referral.status === 'completed' ? (
                                            <span className="px-4 py-2 bg-green-100 text-green-700 rounded-full text-sm font-semibold flex items-center gap-2">
                                                <CheckCircleIcon className="w-4 h-4" />
                                                +1 crédit gagné
                                            </span>
                                        ) : (
                                            <span className="px-4 py-2 bg-orange-100 text-orange-700 rounded-full text-sm font-semibold">
                                                En attente
                                            </span>
                                        )}
                                    </div>
                                </div>
                            ))}
                        </div>
                    </div>
                )}

                {/* CTA */}
                <div className="text-center mt-12">
                    <div className="bg-gradient-to-r from-blue-600 to-purple-600 rounded-2xl p-8 text-white">
                        <h3 className="text-2xl font-bold mb-4">Prêt à parrainer vos amis ?</h3>
                        <p className="text-blue-100 mb-6">
                            Partagez votre lien : vos amis reçoivent un aperçu + 1 crédit bonus, et vous gagnez 1 crédit à leur première analyse.
                        </p>
                        <button
                            onClick={copyToClipboard}
                            className="px-8 py-4 bg-white text-blue-600 font-bold rounded-xl hover:bg-blue-50 transition-all flex items-center gap-2 mx-auto"
                        >
                            <ShareIcon className="w-5 h-5" />
                            Copier mon lien de parrainage
                            <ArrowRightIcon className="w-5 h-5" />
                        </button>
                    </div>
                </div>
            </div>
        </div>
    );
};

export default Referral;

